import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, CheckCircle2, Circle, Clock, Info, KeyRound, Loader2, LogOut, Settings, XCircle } from 'lucide-react';
import { C } from '../theme.js';

 
// ─── Shared UI primitives, in the Disassembly Doc Generator visual system ──


// ── Eyebrow / section header ────────────────────────────────────────────
export function Eyebrow({ children, style }) {
  return (
    <div style={{
      color: C.fg4, fontSize: '.72rem', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '.12em',
      ...style,
    }}>{children}</div>
  );
}

export function SectionTitle({ children, right, style }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      gap: 12, margin: '20px 0 10px', paddingBottom: 6,
      borderBottom: `1px solid ${C.border}`, ...style,
    }}>
      <span style={{ color: C.fg4, fontSize: '.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em' }}>{children}</span>
      {right && <span style={{ color: C.fg5, fontSize: '.75rem' }}>{right}</span>}
    </div>
  );
}

// ── Pill (REQUIRED / CSV / DONE etc) ────────────────────────────────────
export function Pill({ tone = 'opt', size = 'md', icon, children, style }) {
  const tones = {
    req:   { bg: C.accentStrong, fg: '#fff',          bd: 'transparent' },
    opt:   { bg: 'var(--info-bg)',      fg: C.accentBright,  bd: C.accentMuted },
    ok:    { bg: 'var(--ok-bg)',      fg: C.ok,            bd: 'var(--ok-border)' },
    warn:  { bg: 'var(--warn-bg)',      fg: C.warn,          bd: 'var(--warn-border)' },
    error: { bg: 'var(--error-bg)',      fg: C.error,         bd: 'var(--error-border)' },
    mono:  { bg: 'rgba(59,130,246,.08)', fg: C.accentBright, bd: 'transparent' },
    ghost: { bg: 'transparent',  fg: C.fg4,           bd: C.borderStrong },
  };
  const t = tones[tone] || tones.opt;
  const sz = size === 'sm'
    ? { fontSize: '.66rem', padding: '1px 6px', borderRadius: 6 }
    : { fontSize: '.7rem',  padding: '2px 8px', borderRadius: 10 };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontWeight: 700, letterSpacing: '.05em',
      border: `1px solid ${t.bd}`, background: t.bg, color: t.fg,
      whiteSpace: 'nowrap',
      ...sz, ...style,
    }}>
      {icon && <span style={{ fontSize: '.9em' }}>{icon}</span>}
      {children}
    </span>
  );
}

// ── Status chip (queued / processing / done / error / running) ──────────
export function StatusChip({ status, children }) {
  const skins = {
    queued:     { tone: 'opt',   label: 'Queued',     icon: Clock },
    running:    { tone: 'warn',  label: 'Running',    icon: Loader2 },
    processing: { tone: 'warn',  label: 'Processing', icon: Loader2 },
    done:       { tone: 'ok',    label: 'Done',       icon: CheckCircle2 },
    error:      { tone: 'error', label: 'Error',      icon: XCircle },
    draft:      { tone: 'ghost', label: 'Draft',       icon: Circle },
  };
  const s = skins[status] || skins.draft;
  const Icon = s.icon;
  const spinning = status === 'running' || status === 'processing';
  return (
    <Pill tone={s.tone} icon={<Icon size={11} style={spinning ? { animation: 'omSpin 1s linear infinite' } : undefined} />}>
      {children || s.label}
    </Pill>
  );
}

// ── Buttons ─────────────────────────────────────────────────────────────
export function Button({ kind = 'ghost', size = 'md', icon, children, onClick, disabled, style, title }) {
  const [hover, setHover] = React.useState(false);
  const [active, setActive] = React.useState(false);
  const skins = {
    primary: {
      bg: 'linear-gradient(135deg,#1d4ed8,#1e40af)', fg: '#fff', bd: 'transparent',
      hoverOpacity: 0.88,
    },
    secondary: {
      bg: C.surface, fg: C.fg2, bd: C.borderStrong,
      hoverOpacity: 1, hoverBg: C.raised,
    },
    ghost: {
      bg: 'transparent', fg: C.fg3, bd: C.border,
      hoverOpacity: 1, hoverBg: C.surface,
    },
    danger: {
      bg: 'transparent', fg: C.error, bd: 'var(--error-border)',
      hoverOpacity: 1, hoverBg: 'var(--error-bg)',
    },
    success: {
      bg: 'transparent', fg: C.ok, bd: 'var(--ok-border)',
      hoverOpacity: 1, hoverBg: 'var(--ok-bg)',
    },
    accent: {
      bg: 'transparent', fg: C.accentBright, bd: C.accentMuted,
      hoverOpacity: 1, hoverBg: 'var(--info-bg)',
    },
  };
  const s = skins[kind] || skins.ghost;
  const sz = size === 'sm'
    ? { padding: '5px 12px', fontSize: '.78rem' }
    : size === 'lg'
      ? { padding: '11px 22px', fontSize: '.95rem' }
      : { padding: '8px 16px', fontSize: '.85rem' };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        fontFamily: 'inherit', fontWeight: 600,
        background: hover && s.hoverBg ? s.hoverBg : s.bg,
        color: s.fg,
        border: `1px solid ${s.bd}`,
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : (hover ? s.hoverOpacity : 1),
        transform: active ? 'translateY(0)' : (hover && !disabled && kind === 'primary' ? 'translateY(-1px)' : 'translateY(0)'),
        transition: 'opacity .2s, transform .1s, background-color .15s',
        whiteSpace: 'nowrap',
        ...sz,
        ...style,
      }}
    >
      {icon && <span aria-hidden style={{ display: 'inline-flex' }}>{icon}</span>}
      {children}
    </button>
  );
}

// ── IconButton (square) ─────────────────────────────────────────────────
export function IconButton({ children, onClick, title, active, style, disabled }) {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...(disabled ? { opacity: .35, pointerEvents: 'none' } : null),
        width: 30, height: 30, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center',
        background: active ? 'var(--info-bg)' : hover ? C.surface : 'transparent',
        color: active ? C.accentBright : C.fg3,
        border: `1px solid ${active ? C.accentMuted : C.borderStrong}`,
        borderRadius: 8, cursor: 'pointer',
        fontFamily: 'inherit', fontSize: '.85rem',
        transition: 'all .15s',
        ...style,
      }}
    >{children}</button>
  );
}

// ── Inputs ──────────────────────────────────────────────────────────────
const inputBase = {
  width: '100%', background: C.input,
  border: `1px solid ${C.borderStrong}`, borderRadius: 8,
  padding: '8px 11px', color: C.fg1,
  fontSize: '.85rem', fontFamily: 'inherit',
  outline: 'none', transition: 'border-color .15s',
};

export function TextInput({ value, onChange, placeholder, mono, style, type = 'text', onFocus, onBlur }) {
  return (
    <input
      type={type} value={value ?? ''} placeholder={placeholder}
      onChange={(e) => onChange?.(e.target.value)}
      onFocus={onFocus} onBlur={onBlur}
      style={{
        ...inputBase,
        fontFamily: mono ? "'JetBrains Mono', ui-monospace, monospace" : 'inherit',
        ...style,
      }}
    />
  );
}

export function Select({ value, onChange, options, optgroups, style }) {
  const renderOption = (o) => {
    const v = typeof o === 'string' ? o : o.value;
    const l = typeof o === 'string' ? o : o.label;
    return <option key={v} value={v}>{l}</option>;
  };
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value)}
      style={{
        ...inputBase,
        appearance: 'none',
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238ba3bf' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 10px center',
        paddingRight: 28,
        ...style,
      }}
    >
      {optgroups
        ? optgroups.map(g => (
            <optgroup key={g.label} label={g.label}>
              {g.options.map(renderOption)}
            </optgroup>
          ))
        : (options || []).map(renderOption)}
    </select>
  );
}

// Small circled "i" that shows its full text in a floating tooltip on
// hover, instead of that text sitting permanently on the page. Portaled to
// document.body (position: fixed) so it's never clipped by a scrolling or
// overflow:hidden ancestor — e.g. the floating configuration window.
export function InfoIcon({ text, style }) {
  const [rect, setRect] = React.useState(null);
  const ref = React.useRef(null);
  if (!text) return null;

  const show = () => setRect(ref.current.getBoundingClientRect());
  const hide = () => setRect(null);

  const WIDTH = 260;
  const left = rect ? Math.max(8, Math.min(rect.left, window.innerWidth - WIDTH - 8)) : 0;
  const top = rect ? rect.bottom + 6 : 0;

  return (
    <span
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={hide}
      tabIndex={0}
      onFocus={show}
      onBlur={hide}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
        border: `1px solid ${C.fg5}`, color: C.fg5,
        fontSize: '.62rem', fontWeight: 700, fontStyle: 'italic',
        fontFamily: 'Georgia, serif', lineHeight: 1,
        cursor: 'help', userSelect: 'none', outline: 'none',
        ...style,
      }}
    >
      i
      {rect && createPortal(
        <div style={{
          position: 'fixed', left, top, width: WIDTH, zIndex: 3000,
          background: C.surface, border: `1px solid ${C.borderStrong}`,
          borderRadius: 8, padding: '8px 10px',
          boxShadow: '0 8px 24px rgba(0,0,0,.4)',
          color: C.fg3, fontSize: '.74rem', lineHeight: 1.45,
          fontWeight: 400, fontStyle: 'normal', fontFamily: 'Inter, sans-serif',
          pointerEvents: 'none',
        }}>{text}</div>,
        document.body
      )}
    </span>
  );
}

// Field label + optional description. The description used to render as a
// permanent line of text to the right of the label — for anything longer
// than a couple words that ate a lot of vertical space across the panel
// and often wrapped awkwardly. It's now a small "i" icon immediately after
// the label instead; hovering (or focusing, for keyboard users) it shows
// the exact same text in a floating tooltip. Every caller already passes
// this as `hint`, so this is the one place that needed to change.
export function FieldLabel({ children, hint }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '12px 0 6px' }}>
      <span style={{ color: C.fg2, fontSize: '.78rem', fontWeight: 600 }}>{children}</span>
      <InfoIcon text={hint} />
    </div>
  );
}

// ── Card surface ────────────────────────────────────────────────────────
export function Card({ children, accent = false, style, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: C.surface,
        border: `1px solid ${accent ? C.accentMuted : C.border}`,
        borderRadius: 10,
        padding: '16px 18px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color .15s, background-color .15s',
        ...style,
      }}
    >{children}</div>
  );
}

// ── Alert ───────────────────────────────────────────────────────────────
export function Alert({ kind = 'info', children, style }) {
  const skins = {
    info:  { bg: 'var(--info-bg)',      bd: 'var(--accent-muted)', fg: 'var(--info-fg)',  icon: Info },
    warn:  { bg: 'var(--warn-card-bg)', bd: 'var(--warn-border)',  fg: 'var(--warn-fg)',  icon: AlertTriangle },
    ok:    { bg: 'var(--ok-bg)',        bd: 'var(--ok-border)',    fg: 'var(--ok-fg)',    icon: CheckCircle2 },
    error: { bg: 'var(--error-bg)',     bd: 'var(--error-border)', fg: 'var(--error-fg)', icon: XCircle },
  };
  const s = skins[kind];
  const Icon = s.icon;
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.bd}`, borderRadius: 8,
      padding: '10px 14px', color: s.fg, fontSize: '.84rem', lineHeight: 1.55,
      display: 'flex', gap: 10, alignItems: 'flex-start',
      ...style,
    }}>
      <span aria-hidden style={{ flexShrink: 0, display: 'inline-flex', marginTop: 1 }}>
        <Icon size={16} strokeWidth={2} />
      </span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

// ── Inline tooltip-style code block ─────────────────────────────────────
export function Code({ children, style }) {
  return (
    <code style={{
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      fontSize: '.78rem',
      color: C.accentBright,
      background: 'rgba(59,130,246,.08)',
      padding: '1px 6px', borderRadius: 4,
      ...style,
    }}>{children}</code>
  );
}

// ── Logo mark ───────────────────────────────────────────────────────────
// EnvToggle — Test/Prod sliding pill that controls whether the pipeline
// is callable externally (publish/unpublish). Compact enough to live in
// the pipelines table row + the top bar.
export function EnvToggle({ value = 'test', onChange, size = 'md', title }) {
  const isProd = value === 'prod';
  const dims = size === 'sm'
    ? { w: 100, h: 22, pad: 2, fs: 9.5 }
    : { w: 116, h: 26, pad: 3, fs: 10.5 };
  const knobInset = dims.pad;
  const knobStyle = {
    position: 'absolute', top: knobInset, bottom: knobInset,
    width: `calc(50% - ${knobInset}px)`,
    left: isProd ? '50%' : knobInset,
    background: isProd ? C.ok : 'var(--bg-raised)',
    borderRadius: dims.h / 2 - knobInset,
    transition: 'left .18s, background .18s',
    boxShadow: isProd ? '0 0 0 1px rgba(255,255,255,.05) inset' : 'none',
  };
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onChange?.(isProd ? 'test' : 'prod'); }}
      title={title || (isProd
        ? 'PROD — live for external callers. Click to unpublish.'
        : 'TEST — callable only from this UI. Click to publish.')}
      style={{
        position: 'relative', width: dims.w, height: dims.h,
        background: isProd ? 'var(--ok-bg)' : C.input,
        border: `1px solid ${isProd ? 'var(--ok-border)' : C.borderStrong}`,
        borderRadius: dims.h / 2, padding: 0,
        cursor: 'pointer', fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', justifyContent: 'stretch',
        transition: 'background .15s, border-color .15s',
        flexShrink: 0,
      }}
    >
      <span style={knobStyle} />
      <span style={{
        zIndex: 1, flex: 1, textAlign: 'center',
        color: !isProd ? C.fg1 : C.fg5, fontWeight: 700,
        fontSize: dims.fs, letterSpacing: '.1em', textTransform: 'uppercase',
        lineHeight: 1, userSelect: 'none',
      }}>Test</span>
      <span style={{
        zIndex: 1, flex: 1, textAlign: 'center',
        color: isProd ? 'var(--ok-bg)' : C.fg5, fontWeight: 700,
        fontSize: dims.fs, letterSpacing: '.1em', textTransform: 'uppercase',
        lineHeight: 1, userSelect: 'none',
      }}>Prod</span>
    </button>
  );
}

// OrgPicker — header pill that opens a list of orgs to switch to.
// Shows a small mark/initial + the current org slug. Same dropdown style as
// the login screen's switcher.
export function OrgPicker({ orgs = [], current, onSwitch }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  if (!current) return null;
  const active = orgs.find(o => o.name === current) || { name: current, label: current, members: 0, plan: 'Team' };
  const initial = (active.label || active.name).slice(0, 1).toUpperCase();
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={`Organization: ${active.label || active.name}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: open ? C.input : 'transparent',
          border: `1px solid ${open ? C.borderStrong : C.border}`,
          borderRadius: 8, padding: '4px 10px 4px 4px',
          cursor: 'pointer', fontFamily: 'inherit',
          transition: 'background .15s, border-color .15s',
        }}
      >
        <span style={{
          width: 24, height: 24, borderRadius: 5,
          background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
          color: C.accentBright, fontSize: '.74rem', fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>{initial}</span>
        <div style={{ textAlign: 'left', lineHeight: 1.15 }}>
          <div style={{
            color: C.fg6, fontSize: '.6rem',
            textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
          }}>Org</div>
          <div style={{
            color: C.fg2, fontSize: '.78rem', fontWeight: 600,
            fontFamily: "'JetBrains Mono', monospace",
            maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{active.name}</div>
        </div>
        <span style={{ color: C.fg5, fontSize: '.6rem' }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          background: C.app, border: `1px solid ${C.borderStrong}`,
          borderRadius: 10, padding: 4, minWidth: 320,
          boxShadow: '0 12px 32px rgba(0,0,0,.55)',
          zIndex: 60,
          maxHeight: 380, overflowY: 'auto',
        }}>
          <div style={{
            padding: '8px 10px 4px',
            color: C.fg5, fontSize: '.62rem',
            textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
          }}>Switch organization</div>
          {orgs.map(o => {
            const on = o.name === current;
            return (
              <button key={o.id || o.name} type="button"
                onClick={() => { setOpen(false); if (!on) onSwitch?.(o.name); }}
                style={{
                  width: '100%',
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px',
                  background: on ? 'var(--info-bg)' : 'transparent',
                  border: 'none', borderRadius: 6,
                  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                }}
              >
                <span style={{
                  width: 28, height: 28, borderRadius: 6,
                  background: on ? 'var(--info-bg)' : C.input,
                  border: `1px solid ${on ? C.accentMuted : C.borderStrong}`,
                  color: on ? C.accentBright : C.fg3,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '.78rem',
                  flexShrink: 0,
                }}>{(o.label || o.name).slice(0, 1).toUpperCase()}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    color: on ? C.fg1 : C.fg2, fontSize: '.82rem', fontWeight: 600,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{o.label || o.name}</div>
                  <div style={{
                    color: C.fg5, fontSize: '.7rem', marginTop: 1,
                    fontFamily: "'JetBrains Mono', monospace",
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{o.name}{o.members ? ` · ${o.members} member${o.members === 1 ? '' : 's'}` : ''}</div>
                </div>
                {o.plan && (
                  <span style={{
                    background: on ? C.input : 'transparent',
                    border: `1px solid ${on ? C.accentMuted : C.borderSubtle}`,
                    color: on ? C.accentBright : C.fg5,
                    fontSize: '.6rem', fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '.08em',
                    padding: '2px 7px', borderRadius: 4,
                    flexShrink: 0,
                  }}>{o.plan}</span>
                )}
                {on && (
                  <span style={{ color: C.accentBright, display: 'inline-flex' }}><Check size={13} /></span>
                )}
              </button>
            );
          })}
          <div style={{ height: 1, background: C.borderSubtle, margin: '4px 0' }} />
          <button type="button"
            onClick={() => setOpen(false)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px',
              background: 'transparent', border: 'none',
              color: C.accentBright, fontSize: '.78rem', fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
              borderRadius: 6,
            }}
          >
            <span style={{ width: 18, textAlign: 'center' }}>＋</span>
            Create a new organization
          </button>
        </div>
      )}
    </div>
  );
}

// UserPill — avatar + name shown in the top-right of every page.
// Clicking opens a small menu with the signed-in identity + Sign out.
export function UserPill({ user, onLogout }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  if (!user) return null;
  const initials = user.name.split(/\s+/).slice(0, 2).map(s => s[0] || '').join('').toUpperCase() || 'U';
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={`${user.name} · ${user.email}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: open ? C.input : 'transparent',
          border: `1px solid ${open ? C.borderStrong : 'transparent'}`,
          borderRadius: 999, padding: '3px 9px 3px 3px',
          cursor: 'pointer', fontFamily: 'inherit',
          transition: 'background .15s, border-color .15s',
        }}
      >
        <span style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'linear-gradient(135deg,#1d4ed8,#7c3aed)',
          color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: '.74rem', letterSpacing: '.02em',
        }}>{initials}</span>
        <span style={{
          color: C.fg2, fontSize: '.82rem', fontWeight: 600,
          maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{user.name}</span>
        <span style={{ color: C.fg5, fontSize: '.6rem' }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          background: C.app, border: `1px solid ${C.borderStrong}`,
          borderRadius: 10, padding: 4, minWidth: 240,
          boxShadow: '0 12px 32px rgba(0,0,0,.5)',
          zIndex: 50,
        }}>
          <div style={{
            padding: '10px 12px',
            borderBottom: `1px solid ${C.borderSubtle}`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'linear-gradient(135deg,#1d4ed8,#7c3aed)',
              color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: '.85rem', flexShrink: 0,
            }}>{initials}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                color: C.fg1, fontSize: '.86rem', fontWeight: 600,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{user.name}</div>
              <div style={{
                color: C.fg5, fontSize: '.72rem', marginTop: 1,
                fontFamily: "'JetBrains Mono', monospace",
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{user.email}</div>
            </div>
          </div>
          <div style={{ padding: '6px 4px' }}>
            <div style={{
              padding: '4px 10px',
              color: C.fg5, fontSize: '.66rem',
              textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
            }}>Organization</div>
            <div style={{
              padding: '2px 10px 8px',
              color: C.fg2, fontSize: '.78rem',
              fontFamily: "'JetBrains Mono', monospace",
            }}>{user.organization || 'acme-data-eng'}</div>
          </div>
          <div style={{ height: 1, background: C.borderSubtle, margin: '2px 0' }} />
          <button
            onClick={() => setOpen(false)}
            style={menuItemStyle()}
          >
            <span style={{ width: 16, display: 'inline-flex', justifyContent: 'center', color: C.fg4 }}><Settings size={14} /></span>
            Account settings
          </button>
          <button
            onClick={() => setOpen(false)}
            style={menuItemStyle()}
          >
            <span style={{ width: 16, display: 'inline-flex', justifyContent: 'center', color: C.fg4 }}><KeyRound size={14} /></span>
            API keys
          </button>
          <div style={{ height: 1, background: C.borderSubtle, margin: '2px 0' }} />
          <button
            onClick={() => { setOpen(false); onLogout?.(); }}
            style={menuItemStyle({ danger: true })}
          >
            <span style={{ width: 16, display: 'inline-flex', justifyContent: 'center', color: C.error }}><LogOut size={14} /></span>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function menuItemStyle({ danger } = {}) {
  return {
    width: '100%',
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 10px',
    background: 'transparent', border: 'none',
    color: danger ? C.error : C.fg2,
    fontSize: '.82rem', fontWeight: 500,
    cursor: 'pointer', borderRadius: 6,
    fontFamily: 'inherit', textAlign: 'left',
  };
}

export function Logo({ size = 22 }) {
  // A flowing-data mark: three connected nodes
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <circle cx="5"  cy="6"  r="2.4" stroke={C.accentSoft} strokeWidth="1.6" />
      <circle cx="19" cy="6"  r="2.4" stroke={C.accentSoft} strokeWidth="1.6" />
      <circle cx="12" cy="18" r="2.4" fill={C.accent} stroke={C.accent} strokeWidth="1.6" />
      <path d="M5 8.2 L11 16  M19 8.2 L13 16  M7.2 6 L16.8 6" stroke={C.accentSoft} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
